// The tariff formula, at the top of the energy page — every number editable,
// and the whole page recalculates as you type (the parent holds the draft).
// Save stores it on the sensor (core/devices/energySettings.js).

import React, { useState } from 'react';
import {
  Box, Button, Card, Chip, Collapse, IconButton, InputAdornment, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FunctionsIcon from '@mui/icons-material/Functions';
import { formulaText, DEFAULT_TARIFF } from '../../../core/devices/energyTariff';
import { WEEKDAYS } from './energyFormat';

function Num({ label, value, onChange, unit, prefix, width = 132, help, step = 'any' }) {
  return (
    <Tooltip title={help || ''} placement="top" disableHoverListener={!help}>
      <TextField
        size="small"
        type="number"
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        inputProps={{ step, inputMode: 'decimal' }}
        InputProps={{
          startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : null,
          endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : null,
        }}
        sx={{ width, flex: '0 1 auto' }}
      />
    </Tooltip>
  );
}

const Group = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>{title}</Typography>
    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center', pt: 0.75 }}>{children}</Box>
  </Box>
);

export default function TariffEditor({
  settings, onChange, dirty, onSave, onReset, saving, saveNote,
}) {
  const [open, setOpen] = useState(false);
  const t = settings.tariff;
  const setT = (k, v) => onChange({ ...settings, tariff: { ...t, [k]: v } });
  const set = (k, v) => onChange({ ...settings, [k]: v });

  const setSlab = (i, k, v) => {
    const slabs = t.slabs.map((s, j) => (j === i ? { ...s, [k]: v === '' ? null : v } : s));
    setT('slabs', slabs);
  };
  const addSlab = () => {
    const bounded = t.slabs.filter((s) => s.upTo != null);
    const lastCap = bounded.length ? bounded[bounded.length - 1].upTo : 0;
    const open1 = t.slabs[t.slabs.length - 1];
    setT('slabs', [...bounded, { upTo: lastCap + 100, rate: open1.rate }, open1]);
  };
  const removeSlab = (i) => setT('slabs', t.slabs.filter((_, j) => j !== i));
  const toggleOff = (d) => set('offDays', settings.offDays.includes(d)
    ? settings.offDays.filter((x) => x !== d) : [...settings.offDays, d].sort());

  return (
    <Card sx={{ p: 1.5, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <FunctionsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>Tariff formula</Typography>
        {dirty && <Chip size="small" color="warning" label="Not saved" />}
        <Button
          size="small"
          variant={open ? 'outlined' : 'text'}
          startIcon={open ? <ExpandLessIcon /> : <EditIcon />}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Close' : 'Edit'}
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {t.name}
      </Typography>
      <Box
        sx={{
          mt: 1, p: 1, borderRadius: 2, bgcolor: 'action.hover',
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13, lineHeight: 1.5,
          overflowWrap: 'anywhere',
        }}
      >
        {formulaText(t, settings.sanctionedKw)}
        <Box component="span" sx={{ display: 'block', color: 'text.secondary', fontSize: 12 }}>
          U = units in the bill period · B = fixed + energy + wheeling + FAC
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 2 }}>
          <Group title="Charges (from your electricity bill)">
            <TextField
              size="small"
              label="Tariff name"
              value={t.name}
              onChange={(e) => setT('name', e.target.value)}
              sx={{ flex: '1 1 260px' }}
            />
            <Num label="Fixed charge" prefix="₹" unit="/month" value={t.fixed} onChange={(v) => setT('fixed', v)} help="स्थिर आकार" />
            <Num label="+ per kW" prefix="₹" unit="/kW" value={t.fixedPerKw} onChange={(v) => setT('fixedPerKw', v)} help="Only for tariffs charged on sanctioned load; 0 otherwise" />
            <Num label="Wheeling" prefix="₹" unit="/unit" value={t.wheeling} onChange={(v) => setT('wheeling', v)} help="वहन आकार" />
            <Num label="FAC" prefix="₹" unit="/unit" value={t.fac} onChange={(v) => setT('fac', v)} help="इंधन समायोजन आकार — changes every month" />
            <Num label="Electricity duty" unit="%" value={t.dutyPct} onChange={(v) => setT('dutyPct', v)} help="वीज शुल्क — % of fixed + energy + wheeling + FAC" />
            <Num label="ToSE" prefix="₹" unit="/unit" value={t.tose} onChange={(v) => setT('tose', v)} help="वीज विक्री कर — 28.94 paise = 0.2894" />
          </Group>

          <Group title="Energy charge slabs (monthly units)">
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {t.slabs.map((s, i) => {
                const prev = i === 0 ? 0 : t.slabs[i - 1].upTo;
                const isOpen = s.upTo == null;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 64 }}>
                      {isOpen ? `${prev ?? 0}+` : `${prev ?? 0}–`}
                    </Typography>
                    {!isOpen && (
                      <Num label="Up to" unit="units" value={s.upTo} onChange={(v) => setSlab(i, 'upTo', v)} width={130} />
                    )}
                    <Num label="Rate" prefix="₹" unit="/unit" value={s.rate} onChange={(v) => setSlab(i, 'rate', v)} help="वीज आकार" />
                    {!isOpen && (
                      <IconButton size="small" aria-label="Remove slab" onClick={() => removeSlab(i)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
              <Box>
                <Button size="small" startIcon={<AddIcon />} onClick={addSlab}>Add slab</Button>
              </Box>
            </Box>
          </Group>

          <Group title="Payment terms">
            <Num label="Prompt discount" unit="%" value={t.promptPct} onChange={(v) => setT('promptPct', v)} help="% of B, if paid by the prompt date" />
            <Num label="Digital rebate" unit="%" value={t.digitalPct} onChange={(v) => setT('digitalPct', v)} help="% of B, paid online" />
            <Num label="Rebate cap" prefix="₹" value={t.digitalCap} onChange={(v) => setT('digitalCap', v)} />
            <Num label="Late charge" unit="%" value={t.latePct} onChange={(v) => setT('latePct', v)} help="% of the bill, if paid after the due date" />
          </Group>

          <Group title="This connection">
            <Num label="Meter read on day" value={settings.cycleDay} onChange={(v) => set('cycleDay', v)} step={1} width={150} help="The bill period runs from this day to the same day next month" />
            <Num label="Sanctioned load" unit="kW" value={settings.sanctionedKw} onChange={(v) => set('sanctionedKw', v)} width={150} help="मंजूर भार" />
            <Num label="Work starts" unit=":00" value={settings.workStart} onChange={(v) => set('workStart', v)} step={1} width={124} />
            <Num label="Work ends" unit=":00" value={settings.workEnd} onChange={(v) => set('workEnd', v)} step={1} width={124} />
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Weekly off</Typography>
              {WEEKDAYS.map((w, d) => (
                <Chip
                  key={w}
                  size="small"
                  label={w}
                  color={settings.offDays.includes(d) ? 'primary' : 'default'}
                  variant={settings.offDays.includes(d) ? 'filled' : 'outlined'}
                  onClick={() => toggleOff(d)}
                />
              ))}
            </Box>
          </Group>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="contained" onClick={onSave} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={onReset} disabled={saving || !dirty}>Undo changes</Button>
            <Button
              color="inherit"
              onClick={() => onChange({ ...settings, tariff: { ...DEFAULT_TARIFF } })}
              disabled={saving}
            >
              MSEDCL LT-II defaults
            </Button>
          </Box>
        </Box>
      </Collapse>
      {saveNote && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: saveNote.ok ? 'success.main' : 'warning.main' }}>
          {saveNote.text}
        </Typography>
      )}
    </Card>
  );
}
